if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.formInput = this.form?.querySelector("[name=id]");
        if (this.formInput) this.formInput.disabled = false;
        this.form?.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart = document.querySelector("cart-drawer");
        this.cartType = this.cart?.dataset.cartType || "page";

        this.submitButton = this.querySelector('[type="submit"]');
        this.body = document.querySelector("body");

        if (document.querySelector("cart-drawer"))
          this.submitButton?.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";

        this.handleErrorMessage = this.handleErrorMessage.bind(this);
        this.redirectAfterSubmit = this.redirectAfterSubmit.bind(this);
        this.disableLoadingInButton = this.disableLoadingInButton.bind(this);
      }

      closeComplementaryProduct() {
        const complementaryOverlayPage = document.querySelector(
          ".wt__quick-buy--page-overlay",
        );
        if (this.body.classList.contains("quick-buy-page-overlay"))
          this.body.classList.remove("quick-buy-page-overlay");
        if (
          complementaryOverlayPage.classList.contains(
            "wt__quick-buy--page-overlay--open",
          )
        )
          complementaryOverlayPage.classList.remove(
            "wt__quick-buy--page-overlay--open",
          );
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();
        this.cart?.setActiveElement(document.activeElement);

        const loader = this.querySelector(".loading-overlay__spinner");

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        if (loader) loader.classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        let sectionsToRender = [];
        if (this.cart && typeof this.cart.getSectionsToRender === "function") {
          sectionsToRender = this.cart
            .getSectionsToRender()
            .map((section) => section.id);
          formData.append("sections", sectionsToRender);
        } else {
          formData.append("sections", []);
        }

        formData.append("sections_url", window.location.pathname);

        // Shopify's `shopify-additional-items` form fields are consumed by
        // product-page accelerated checkout, but the standard Cart AJAX form
        // path ignores them. When the paid warranty is explicitly selected,
        // send the lamp and its warranty together through Cart AJAX's native
        // multi-item JSON contract. `parent_id` creates a nested relationship:
        // the warranty follows the lamp through cart/checkout/order and is
        // removed automatically if the parent lamp line is removed.
        //
        // This branch is narrowly gated by our enabled warranty field, so every
        // other product form keeps Velour's original FormData behavior.
        const warrantyField = this.form.querySelector(
          '[data-flowglow-warranty-field="id"]:not([disabled])',
        );
        if (warrantyField) {
          const lampVariantId = Number(formData.get("id"));
          const lampQuantity = Math.max(
            1,
            Number(formData.get("quantity")) || 1,
          );
          // The paid extension is defined one-for-one with the lamp. Read the
          // canonical form quantity here instead of trusting a mirrored hidden
          // value that could be stale at the exact moment of submission.
          const warrantyQuantity = lampQuantity;
          const lampItem = {
            id: lampVariantId,
            quantity: lampQuantity,
          };

          // Preserve any regular line-item properties / selling plan that the
          // primary form may carry. The FlowGlow lamp currently has neither,
          // but retaining them keeps this generic theme component correct.
          const lampProperties = {};
          for (const [key, value] of formData.entries()) {
            const propertyMatch = key.match(/^properties\[(.+)\]$/);
            if (propertyMatch && value !== "") {
              lampProperties[propertyMatch[1]] = value;
            }
          }
          if (Object.keys(lampProperties).length) {
            lampItem.properties = lampProperties;
          }
          const sellingPlan = formData.get("selling_plan");
          if (sellingPlan) lampItem.selling_plan = Number(sellingPlan);

          const warrantyGroup = warrantyField.closest(
            "[data-flowglow-warranty-fields]",
          );
          const warrantyGroupToken = warrantyGroup.querySelector(
            '[data-flowglow-warranty-field="parent_group"]',
          )?.value;
          const warrantyItem = {
            id: Number(warrantyField.value),
            quantity: warrantyQuantity,
            parent_id: lampVariantId,
            properties: {
              _flowglow_terms_version:
                warrantyGroup.dataset.warrantyTermsVersion,
              ...(warrantyGroupToken
                ? { _flowglow_warranty_group: warrantyGroupToken }
                : {}),
            },
          };

          config.headers["Content-Type"] = "application/json";
          config.body = JSON.stringify({
            items: [lampItem, warrantyItem],
            sections: sectionsToRender,
            sections_url: window.location.pathname,
          });
        } else {
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.error = true;

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");

              this.dispatchEvent(
                new CustomEvent("cart-drawer:refresh", {
                  detail: { response: response },
                  bubbles: true,
                  composed: true,
                }),
              );

              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButton.querySelector("span").classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.redirectAfterSubmit(response);
                  });
                },
                { once: true },
              );
              if (this.cartType === "drawer") quickAddModal.hide(true);
            } else {
              const isClosedCart = !document.body.classList.contains(
                "page-overlay-cart-on",
              );
              this.redirectAfterSubmit(response, isClosedCart);
            }
          })
          .catch((e) => {
            console.error(e);
            if (e instanceof TypeError && e.message.includes("'cart-drawer'")) {
              location.reload();
            }
          })
          .finally(() => {
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            this.submitButton.removeAttribute("aria-disabled");
            if (this.error) {
              this.disableLoadingInButton();
              return;
            }
            this.quick_add_container = document.querySelector(
              ".wt__quick-buy__container",
            );
            this.quick_add_overlay = document.querySelector(
              ".wt__quick-buy--page-overlay",
            );
            this.quick_add_product =
              this.quick_add_container.querySelector(".wt-product");
            this.loader = this.quick_add_container.querySelector(
              ".wt__quick-buy-loader",
            );

            if (!this.quick_add_container.classList.contains("hidden")) {
              // use handle and quickbuy closeCart()

              this.quick_add_product?.remove();
              this.quick_add_container.classList.remove(
                "wt__quick-buy__container--open",
              );
              this.quick_add_overlay.classList.remove(
                "wt__quick-buy--page-overlay--open",
              );
              document.body.classList.remove("quick-buy-page-overlay");
              this.loader.classList.remove("hidden");
            }

            // delay button loading
            setTimeout(() => {
              this.disableLoadingInButton();
            }, 200);
          });
      }

      disableLoadingInButton() {
        this.submitButton.classList.remove("loading");
        this.querySelector(".loading-overlay__spinner").classList.add("hidden");
      }

      redirectAfterSubmit(response, isClosedCart = true) {
        const body = document.body;
        const isCartPage = body.classList.contains("template-cart");

        if (this.cartType === "drawer" && isCartPage) {
          const overlay = document.querySelector(
            ".wt__quick-buy--page-overlay",
          );
          overlay.classList.remove("wt__quick-buy--page-overlay--open");
          body.classList.remove("quick-buy-page-overlay");
        } else if (this.cartType === "drawer") {
          this.cart.renderContents(response, isClosedCart);
          return;
        }
        window.location = window.routes.cart_url;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    },
  );
}
